import React from 'react';
import { LensePanel, type LenseItem } from '../../organisms/LensePanel';
import { Breadcrumb, type BreadcrumbItem } from '../../molecules/Breadcrumb';
import './MyFarmPageLayout.css';

export interface MyFarmPageLayoutProps {
  /** Category items for the selector panel */
  categories: LenseItem[];
  /** Currently selected category ID */
  selectedCategory?: string;
  /** Currently selected subcategory ID */
  selectedSubcategory?: string | null;
  /** Handler for category selection */
  onCategorySelect?: (categoryId: string, subcategoryId: string | null) => void;
  /** Root label for breadcrumb (e.g., "My Farm") */
  rootLabel?: string;
  /** Optional class name */
  className?: string;
  /**
   * Suppresses the left LensePanel, hides the in-content breadcrumb
   * header, and zeros the desktop left-margin reservation. The
   * starting-page surface (no lense selected) uses this so the tile
   * grid is the only navigator on screen. Lens-scoped surfaces keep
   * the panel + breadcrumb visible. Mirrors the post-#919
   * `hideLensePanel` prop on `DocumentsPageLayout`.
   */
  hideLensePanel?: boolean;
  /** Title rendered at the top of the LensePanel (e.g. "Digital Twin"). */
  lensePanelTitle?: string;
  /** Content */
  children?: React.ReactNode;
}

/** Default categories for My Farm section */
export const myFarmCategories: LenseItem[] = [
  { id: 'overview', name: 'Farm Overview' },
  {
    id: 'livestock',
    name: 'Livestock',
    children: [
      { id: 'cattle', name: 'Cattle' },
      { id: 'pigs', name: 'Pigs' },
      { id: 'poultry', name: 'Poultry' },
    ],
  },
  {
    id: 'fields',
    name: 'Fields & Crops',
    children: [
      { id: 'field-map', name: 'Field Map' },
      { id: 'crop-plan', name: 'Crop Plan' },
      { id: 'soil-data', name: 'Soil Data' },
    ],
  },
  {
    id: 'nutrients',
    name: 'Nutrient Management',
    children: [
      { id: 'fertilizer', name: 'Fertilizer Records' },
      { id: 'manure', name: 'Manure Tracking' },
      { id: 'balance', name: 'Nutrient Balance' },
    ],
  },
  {
    id: 'equipment',
    name: 'Equipment',
    children: [
      { id: 'machinery', name: 'Machinery' },
      { id: 'maintenance', name: 'Maintenance Log' },
    ],
  },
  { id: 'personnel', name: 'Personnel' },
];

/**
 * MyFarmPageLayout - Layout for the My Farm section.
 * Features a selector panel with farm data categories and a content area.
 */
export const MyFarmPageLayout: React.FC<MyFarmPageLayoutProps> = ({
  categories = myFarmCategories,
  selectedCategory = 'overview',
  selectedSubcategory = null,
  onCategorySelect,
  rootLabel = 'My Farm',
  className = '',
  hideLensePanel = false,
  lensePanelTitle,
  children,
}) => {
  // Build breadcrumb items from selection
  const buildBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [{ label: rootLabel }];

    const category = categories.find(c => c.id === selectedCategory);
    if (category) {
      // If subcategory is selected, make category clickable
      if (selectedSubcategory && category.children) {
        items.push({
          label: category.name,
          onClick: () => onCategorySelect?.(selectedCategory, null),
        });

        const subcategory = category.children.find(s => s.id === selectedSubcategory);
        if (subcategory) {
          items.push({ label: subcategory.name });
        }
      } else {
        items.push({ label: category.name });
      }
    }

    return items;
  };

  const rootClassName = [
    'my-farm-page-layout',
    hideLensePanel ? 'my-farm-page-layout--no-lens' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClassName}>
      {/* Selector Panel — suppressed on the starting-page surface. */}
      {!hideLensePanel && (
        <LensePanel
          title={lensePanelTitle}
          items={categories}
          selectedItem={selectedCategory}
          selectedSubItem={selectedSubcategory}
          onSelect={onCategorySelect}
          className="my-farm-page-layout__selector"
        />
      )}

      {/* Main Content Area */}
      <div className="my-farm-page-layout__main">
        {!hideLensePanel && (
          <header className="my-farm-page-layout__header">
            <Breadcrumb items={buildBreadcrumbItems()} />
          </header>
        )}
        <div className="my-farm-page-layout__content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default MyFarmPageLayout;
